<script setup>
import { AppIcon } from '@/components/shared'

/**
 * Shared social link chips renderer. Used on `/@username`, `/mista/{slug}`,
 * `/galerie/{slug}` - anywhere a polymorphic `social_links` array needs to
 * surface as clickable platform pills.
 *
 * PLATFORM_META mirrors `App\Enums\SocialLinkPlatform::getLabel/getIcon`
 * server-side. Adding a new platform = one entry here + one case in the
 * PHP enum. The custom `label` field (only set for the `other` platform)
 * takes precedence over the canonical platform name.
 */
defineProps({
    links: {
        type: Array,
        default: () => [],
    },
    size: {
        type: String,
        default: 'md', // 'sm' | 'md'
        validator: (v) => ['sm', 'md'].includes(v),
    },
})

const PLATFORM_META = {
    instagram: { label: 'Instagram', icon: 'photo_camera_front' },
    facebook: { label: 'Facebook', icon: 'thumb_up' },
    twitter: { label: 'X', icon: 'tag' },
    tiktok: { label: 'TikTok', icon: 'music_note' },
    youtube: { label: 'YouTube', icon: 'play_circle' },
    whatsapp: { label: 'WhatsApp', icon: 'chat' },
    website: { label: 'Web', icon: 'public' },
    other: { label: 'Odkaz', icon: 'link' },
}

function platformLabel(link) {
    if (link.label) return link.label
    return PLATFORM_META[link.platform]?.label ?? 'Odkaz'
}

function platformIcon(link) {
    return PLATFORM_META[link.platform]?.icon ?? 'link'
}
</script>

<template>
    <div
        class="flex flex-wrap gap-2"
        v-if="links.length > 0"
    >
        <a
            class="border-outline-variant/30 bg-surface-container-low text-on-surface-variant hover:bg-surface-container-high hover:text-on-surface inline-flex items-center gap-2 rounded-md border font-bold tracking-wider uppercase transition-colors"
            v-for="link in links"
            :key="`${link.platform}-${link.url}`"
            :class="size === 'sm' ? 'px-3 py-1.5 text-xs' : 'px-4 py-3 text-sm'"
            :href="link.url"
            target="_blank"
            rel="noopener noreferrer"
            :title="platformLabel(link)"
        >
            <AppIcon
                :name="platformIcon(link)"
                :class="size === 'sm' ? 'text-sm' : 'text-base'"
            />
            {{ platformLabel(link) }}
        </a>
    </div>
</template>
